import type { AutomationQuickFilter, QueueFacets } from '@/lib/types';
import { businessTodayParam } from '@/lib/businessDate';
import { cn } from '@/lib/utils';

interface QueueQuickFiltersProps {
  filters: AutomationQuickFilter[];
  activeParams: URLSearchParams;
  facets?: QueueFacets;
  onApply: (filter: AutomationQuickFilter) => void;
}

function countFor(filter: AutomationQuickFilter, facets?: QueueFacets): number | null {
  if (!facets) return null;
  if (filter.facetKey && typeof facets[filter.facetKey] === 'number') {
    return facets[filter.facetKey] as number;
  }
  const bucket = filter.params['bucket'];
  if (!bucket || !facets.bucket_counts) return null;
  const buckets = bucket.split(',').map((b) => b.trim()).filter(Boolean);
  const total = buckets.reduce((sum, b) => sum + (facets.bucket_counts?.[b] ?? 0), 0);
  return buckets.length > 0 ? total : null;
}

function isActive(filter: AutomationQuickFilter, activeParams: URLSearchParams): boolean {
  return Object.entries(filter.params).every(
    ([key, value]) => activeParams.get(key) === resolveValue(value),
  );
}

function resolveValue(value: string): string {
  return value === '$today' ? businessTodayParam() : value;
}

export function QueueQuickFilters({
  filters,
  activeParams,
  facets,
  onApply,
}: QueueQuickFiltersProps) {
  if (filters.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-8">
      <span className="text-sm font-medium text-neutral-700">Views</span>
      {filters.map((filter) => {
        const active = isActive(filter, activeParams);
        const count = countFor(filter, facets);
        return (
          <button
            key={filter.key}
            type="button"
            aria-pressed={active}
            onClick={() => onApply(filter)}
            className={cn(
              'inline-flex h-9 items-center gap-6 rounded-md border px-10 text-sm font-medium transition-colors',
              active
                ? 'border-primary-500 bg-primary-500/10 text-primary-700'
                : 'border-neutral-200 bg-neutral-0 text-neutral-700 hover:bg-neutral-50',
            )}
          >
            <span>{filter.label}</span>
            {count !== null && (
              <span className="rounded bg-neutral-100 px-6 py-1 text-xs text-neutral-600">
                {count.toLocaleString()}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
